import { useState, useEffect } from 'react';
import { Box, Grid, LinearProgress } from '@mui/material';
import { useHistory } from 'react-router';
import { MFSubmitButton, MFTextField } from '../../lib/formik';
import { Formik } from 'formik';
import { AddAMCApproverRequestBody, AmcApprover } from '../../redux-store/types/api-types';
import { useDispatch } from 'react-redux';
import {
  addAmcApprover,
  AmcApproverAccess,
  getAMCApproverById,
  getAmcApproverAccess,
  UpdateAmcApprover,
} from '../../redux-store/actions/userManagement';
import { amcApproverSchema } from '../../utils/schema';
import { Location } from 'history';
import { CountryCodesDropDown } from '../commonComponents';
import { AddressFieldEditable } from './AddressFields';
import { styles } from '../../Styles/styles';
import { BackButtonAndHeader } from './userBackButton';
import { nonDigitRegex } from '../../utils/regex';

const initialValues: AddAMCApproverRequestBody = {
  name: '',
  email: '',
  countryNameAndCode: 'India: +91',
  countryCode: '+91',
  phone: '',
  panNumber: '',
  pincode: '',
  buildingNo: '',
  streetName: '',
  city: '',
  state: '',
  country: '',
  isActive: true,
};

export default function AddAMCApprover({
  location,
}: {
  location: Location<{ amcApproverId: number }>;
}): JSX.Element {
  const { amcApproverId } = location.state || {};
  const history = useHistory();
  const dispatch = useDispatch();
  const [accessForEditAdd, setAccessForEditAdd] = useState(false);

  const [amcApproverDetails, setAmcApproverDetails] = useState(initialValues);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let isComponentAlive = true;
    (async function () {
      try {
        setLoading(true);
        const { amcApproverAccess } = (await dispatch(
          getAmcApproverAccess()
        )) as unknown as AmcApproverAccess;
        if (amcApproverId) {
          const getAmcApproverDetails = (await dispatch(
            getAMCApproverById(amcApproverId)
          )) as unknown as AmcApprover;
          const { pincode, buildingNo, streetName, city, state, country, panNumber, id, isActive } =
            getAmcApproverDetails;
          const {
            firstName: name,
            phone,
            email,
            countryCode,
            countryNameAndCode,
          } = getAmcApproverDetails.user;
          setAmcApproverDetails({
            ...amcApproverDetails,
            name,
            phone,
            email,
            pincode,
            buildingNo,
            streetName,
            city,
            state,
            country,
            panNumber,
            id,
            isActive,
            countryNameAndCode: countryNameAndCode ? countryNameAndCode : 'India: +91',
            countryCode: countryCode ? countryCode : '+91',
          });
        }
        if (!isComponentAlive) return;
        setAccessForEditAdd(amcApproverAccess);
        setLoading(false);
      } catch (e) {
        console.error((e as Error).message);
      } finally {
        if (isComponentAlive) {
          setLoading(false);
        }
      }
    })();

    return () => {
      isComponentAlive = false;
    };
  }, []);

  const onSubmit = async (values: AddAMCApproverRequestBody) => {
    try {
      if (!amcApproverId) {
        await dispatch(
          addAmcApprover({
            ...values,
            countryCode: values.countryNameAndCode?.split(':')[1].trim(),
          })
        );
        history.push('amc-approver');
      } else {
        await dispatch(
          UpdateAmcApprover(amcApproverId, {
            ...values,
            countryCode: values.countryNameAndCode?.split(':')[1].trim(),
          })
        );
        history.push('amc-approver-details', { amcApproverId });
      }
    } catch (e) {
      console.error((e as Error).message);
    }
  };

  return (
    <>
      <Formik
        initialValues={amcApproverDetails}
        onSubmit={onSubmit}
        enableReinitialize={true}
        validationSchema={amcApproverSchema}>
        {({ handleSubmit, values }) => (
          <Box sx={styles.UsersFormikBox} component="form" noValidate onSubmit={handleSubmit}>
            <BackButtonAndHeader
              User={amcApproverId ? 'AMC Approver' : 'Onboarding AMC Approver'}
              onClickBack={() =>
                amcApproverId
                  ? history.push('amc-approver-details', { amcApproverId: values.id })
                  : history.push('amc-approver')
              }
            />
            {loading ? (
              <LinearProgress />
            ) : (
              <>
                <Grid container rowSpacing={1} columnSpacing={4} px={4} pt={3}>
                  <Grid item xs={12} sm={6} md={4}>
                    <MFTextField
                      name="name"
                      label="Name *"
                      placeholder="Enter Name"
                      disabled={!accessForEditAdd}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <MFTextField
                      name="email"
                      label="Email Id *"
                      placeholder="Enter Email Id"
                      disabled={!accessForEditAdd}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <MFTextField
                      name="phone"
                      label="Mobile Number *"
                      placeholder="Enter Mobile Number"
                      regexForFilterValue={nonDigitRegex}
                      disabled={!accessForEditAdd}
                    />
                  </Grid>
                  <AddressFieldEditable accessForEditAdd={accessForEditAdd} />
                </Grid>
                {accessForEditAdd && (
                  <Box sx={{ width: '100%', maxWidth: '350px', mx: 'auto', mt: 3 }}>
                    <MFSubmitButton label="Save" />
                  </Box>
                )}
              </>
            )}
          </Box>
        )}
      </Formik>
    </>
  );
}
